/**
 * DescriptionList component for the Indigo design system.
 *
 * A two-column term → description list used for summary read-outs like the Case
 * Overview "At a glance" / "Claim info" block. The term is a bold `Heading`, the
 * description is `Body`; both use the secondary text color. `size` picks the
 * scale: `md` (14px, the default) for canvas read-outs, `sm` (12px) for denser
 * surfaces such as panels and modals. The term column has a fixed width; descriptions wrap and may contain
 * inline nodes (e.g. a status chip).
 *
 * @example
 * ```tsx
 * <DescriptionList
 *   items={[
 *     { term: "Claim info", description: <>Specific trauma; DOI: 09/12/2024</> },
 *     { term: "Demographics", description: "39 y/o male, warehouse worker" },
 *   ]}
 * />
 * ```
 */
import { clsx } from "clsx";
import { forwardRef, type ReactNode } from "react";
import { Body } from "../Typography/Body";
import { Heading } from "../Typography/Heading";
import "./DescriptionList.css";

export interface DescriptionListItem {
  /** Left-column term. */
  term: ReactNode;
  /** Right-column description. */
  description: ReactNode;
}

export type DescriptionListSize = "md" | "sm";

export interface DescriptionListProps {
  /** Rows to render. */
  items: DescriptionListItem[];

  /**
   * Width of the term column (any CSS length).
   * @default "160px"
   */
  termWidth?: string;

  /**
   * Text scale: `md` is 14px, `sm` is 12px.
   * @default "md"
   */
  size?: DescriptionListSize;

  /** Additional CSS classes applied to the list. */
  className?: string;
}

export const DescriptionList = forwardRef<HTMLDivElement, DescriptionListProps>(
  ({ items, termWidth = "160px", size = "md", className }, ref) => {
    return (
      <div
        ref={ref}
        className={clsx("indigo-description-list", className)}
        style={
          {
            "--indigo-description-list-term-width": termWidth,
          } as React.CSSProperties
        }
      >
        {items.map((item, index) => (
          <div key={index} className="indigo-description-list__row">
            <Heading
              level={size === "sm" ? 4 : 3}
              className="indigo-description-list__term text-indigo-text-secondary"
            >
              {item.term}
            </Heading>
            <Body
              size={size}
              className="indigo-description-list__description text-indigo-text-secondary"
            >
              {item.description}
            </Body>
          </div>
        ))}
      </div>
    );
  },
);

DescriptionList.displayName = "DescriptionList";
